import { QueryCombinerType } from 'module/api/filter/types';
import { unreachable } from 'module/helpers/unreachable';
import { AssigneeFieldFilter } from './assignee';
import { BooleanFieldFilter } from './boolean';
import { DateFieldFilter } from './date';
import { EstimateFieldFilter } from './estimate';
import { MultiUserFieldFilter } from './multi-user';
import { NumberFieldFilter } from './number';
import { SingleSelectFieldFilter } from './single-select';
import { SingleUserFieldFilter } from './single-user';
import { TextFieldFilter } from './text';
import { FieldType } from '../field/field';
import {
  AssigneeField,
  BooleanField,
  DateField,
  EstimateField,
  FilterableField,
  MultiSelectField,
  MultiUserField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from '../field/filterable-field';
import { TermSeparator } from '../separators';
import { MultiSelectFieldFilter } from './multi-select';

export enum AiFieldFilterType {
  Ai = 'ai',
}
export interface AiFieldFilter {
  id: string;
  value: string;
  isAiGenerated: boolean;
  type: AiFieldFilterType;
  fieldName?: string;
}

export type FieldFilter = (
  | BooleanFieldFilter
  | NumberFieldFilter
  | TextFieldFilter
  | DateFieldFilter
  | SingleSelectFieldFilter
  | MultiSelectFieldFilter
  | SingleUserFieldFilter
  | MultiUserFieldFilter
  | AssigneeFieldFilter
  | EstimateFieldFilter
) & {
  // uses uuid for key prop in component
  // needed for dynamic conditions in template
  // where the filter conditions can be repetitive
  // resulting in duplicate key.
  id?: string;
};

const isValid = (filter: FieldFilter): boolean => {
  switch (filter.field?.type) {
    case FieldType.Boolean:
      return BooleanFieldFilter.isValid(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFieldFilter.isValid(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFieldFilter.isValid(filter as DateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFieldFilter.isValid(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFieldFilter.isValid(filter as SingleSelectFieldFilter);
    case FieldType.MultiSelect:
      return MultiSelectFieldFilter.isValid(filter as MultiSelectFieldFilter);
    case FieldType.Assignee:
      return AssigneeFieldFilter.isValid(filter as AssigneeFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFieldFilter.isValid(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFieldFilter.isValid(filter as MultiUserFieldFilter);
    case FieldType.Estimate:
      return EstimateFieldFilter.isValid(filter as EstimateFieldFilter);
    default:
      return unreachable(filter.field as never);
  }
};

const toCriteriaString = (filter: FieldFilter) => {
  switch (filter.field?.type) {
    case FieldType.Boolean:
      return BooleanFieldFilter.toString(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFieldFilter.toString(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFieldFilter.toString(filter as DateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFieldFilter.toString(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFieldFilter.toString(
        filter as SingleSelectFieldFilter,
      );
    case FieldType.MultiSelect:
      return MultiSelectFieldFilter.toString(filter as MultiSelectFieldFilter);
    case FieldType.Assignee:
      return AssigneeFieldFilter.toString(filter as AssigneeFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFieldFilter.toString(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFieldFilter.toString(filter as MultiUserFieldFilter);
    case FieldType.Estimate:
      return EstimateFieldFilter.toString(filter as EstimateFieldFilter);
    default:
      return unreachable(filter.field as never);
  }
};

const toString = (filter: FieldFilter): string => {
  return [filter.field.fieldName, toCriteriaString(filter)].join(TermSeparator);
};

const parse = (field: FilterableField, filter: string): FieldFilter => {
  switch (field.type) {
    case FieldType.Boolean:
      return BooleanFieldFilter.parse(filter, field as BooleanField);
    case FieldType.Number:
      return NumberFieldFilter.parse(filter, field as NumberField);
    case FieldType.Date:
      return DateFieldFilter.parse(filter, field as DateField);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFieldFilter.parse(filter, field as TextField);
    case FieldType.SingleSelect:
      return SingleSelectFieldFilter.parse(filter, field as SingleSelectField);
    case FieldType.MultiSelect:
      return MultiSelectFieldFilter.parse(filter, field as MultiSelectField);
    case FieldType.Assignee:
      return AssigneeFieldFilter.parse(filter, field as AssigneeField);
    case FieldType.SingleUser:
      return SingleUserFieldFilter.parse(filter, field as SingleUserField);
    case FieldType.MultiUser:
      return MultiUserFieldFilter.parse(filter, field as MultiUserField);
    case FieldType.Estimate:
      return EstimateFieldFilter.parse(filter, field as EstimateField);

    default:
      return unreachable(field);
  }
};

export const FieldFilter = {
  isValid,
  toString,
  parse,
};

export interface Filter {
  type: QueryCombinerType;
  criteria: FieldFilter[];
}

export const defaultFilter: Filter = {
  type: QueryCombinerType.All,
  criteria: [],
};
